// Helper types to extract keys by their value type
type KeysOfType<T, V> = {
  [K in keyof T]-?: T[K] extends V | undefined ? K : never;
}[keyof T];

// Generic key types
export type BooleanKeysOf<T> = KeysOfType<T, boolean>;
export type NumberKeysOf<T> = KeysOfType<T, number>;
export type EnumKeysOf<T> = Exclude<
  keyof T,
  BooleanKeysOf<T> | NumberKeysOf<T>
>;

const getDisplayValue = <T>(
  value: T | null | undefined,
  formatter?: (value: NonNullable<T>) => string
): string | undefined => {
  if (value == null) {
    return undefined;
  }
  if (formatter) {
    return formatter(value as NonNullable<T>);
  }
  return String(value);
};

// Generic display config that works for all value types
type GenericDisplayConfig<TSettings, K extends keyof TSettings> = {
  field: K;
  label: string;
  groupTitle?: string;
  defaultValue?: TSettings[K];
  formatter?: (value: NonNullable<TSettings[K]>) => string;
};

/**
 * Generic factory for creating display functions for any setting type.
 * Consolidates boolean, number, and enum display function creation.
 *
 * @param getTemplateSettings - Accessor to extract template settings from args
 * @param getWorkspaceSettings - Accessor to extract workspace settings from args
 *
 * @example
 * const createDisplayFn = createDisplayFnFactory<
 *   LinePlotTemplateSettings,
 *   LinePlotDisplayFnArgs
 * >(
 *   args => args.currTemplateLinePlotSettings,
 *   args => args.currWorkspaceLinePlotSettings
 * );
 *
 * const getShowLegendDisplayRow = createDisplayFn({
 *   field: 'showLegend',
 *   label: 'Show legend',
 *   defaultValue: DEFAULT_LINE_PLOT_SETTINGS.showLegend,
 * });
 *
 * const getMaxRunsDisplayRow = createDisplayFn({
 *   field: 'maxRuns',
 *   label: 'Max runs',
 *   defaultValue: DEFAULT_LINE_PLOT_SETTINGS.limit,
 * });
 *
 * const getGroupAggDisplayRow = createDisplayFn({
 *   field: 'groupAgg',
 *   label: 'Group aggregation',
 *   formatter: (value) => capitalizeFirst(value),
 * });
 */
export const createDisplayFnFactory = <TSettings, TArgs>(
  getTemplateSettings: (args: TArgs) => Partial<TSettings> | null | undefined,
  getWorkspaceSettings: (args: TArgs) => Partial<TSettings> | null | undefined
) => {
  return <K extends keyof TSettings>(
    config: GenericDisplayConfig<TSettings, K>
  ) => {
    return (args: TArgs) => {
      const templateSettings = getTemplateSettings(args);
      const workspaceSettings = getWorkspaceSettings(args);

      const currValue = templateSettings?.[config.field];
      const newValue = workspaceSettings?.[config.field] ?? config.defaultValue;

      return {
        ...(config.groupTitle ? {groupTitle: config.groupTitle} : {}),
        label: config.label,
        field: config.field,
        currValue,
        currDisplayValue: getDisplayValue(
          currValue as TSettings[K] | undefined,
          config.formatter
        ),
        newValue,
        newDisplayValue: getDisplayValue(
          newValue as TSettings[K] | undefined,
          config.formatter
        ),
      };
    };
  };
};
